import { useState } from "react";
import { MapPin, Search } from "lucide-react";
import { api } from "../../api";
import type { ClientLocation, Place } from "../../keywords";
import { US } from "./shared";

type Found = { geo: string; label: string; type: string | null };

/**
 * Which place the numbers are for: the client's own cities (resolved to Google
 * locations by the server), the whole US, or any place found in Google's list.
 */
export function LocationPicker({ locations, value, onChange }: { locations: ClientLocation[]; value: Place; onChange: (p: Place) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Found[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const own: Place[] = locations.filter((l) => l.geo).map((l) => ({ geo: l.geo!, geo_label: l.geo_label!, location_id: l.location_id }));
  const options = [...own, US];
  if (!options.some((o) => o.geo === value.geo)) options.unshift(value);

  async function find() {
    if (q.trim().length < 2) { setError("Type at least two letters of a place."); return; }
    setBusy(true); setError("");
    try {
      const r = await api<{ locations: Found[] }>(`/api/keywords/locations?q=${encodeURIComponent(q.trim())}`);
      setFound(r.locations);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not search places"); }
    finally { setBusy(false); }
  }

  return (
    <div className="kw-location">
      <label className="kw-inline-field">
        <MapPin aria-hidden="true" size={14} />
        <span className="sr-only">Numbers for</span>
        <select value={value.geo} onChange={(e) => onChange(options.find((o) => o.geo === e.target.value) || US)} aria-label="Numbers for">
          {options.map((o) => <option key={o.geo} value={o.geo}>{o.geo_label}</option>)}
        </select>
      </label>
      <button type="button" className="text-button" aria-expanded={open} onClick={() => { setOpen(!open); setFound(null); setError(""); }}>
        {open ? "Cancel" : "Other place…"}
      </button>
      {open && (
        <div className="kw-location-search">
          <div className="kw-location-row">
            <input type="search" placeholder="City, metro or state" aria-label="Find a place" value={q} autoFocus
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void find(); } }} />
            <button type="button" className="button secondary" disabled={busy} onClick={() => void find()}><Search aria-hidden="true" />{busy ? "Finding…" : "Find"}</button>
          </div>
          {error && <p role="alert" className="kw-error">{error}</p>}
          {found && (found.length ? (
            <ul className="kw-location-results">
              {found.map((f) => (
                <li key={f.geo}>
                  <button type="button" className="text-button" onClick={() => { onChange({ geo: f.geo, geo_label: f.label, location_id: null }); setOpen(false); setQ(""); setFound(null); }}>
                    {f.label}{f.type ? <small> · {f.type}</small> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : <p className="muted">Google has no place by that name.</p>)}
        </div>
      )}
    </div>
  );
}
